<template>
  <v-app>
    <!-- Global Loader -->
    <PageLoader v-if="useVendorStore.globalLoading" />
    
    <router-view/>
    <v-overlay
      v-model="updateAvailable"
      class="align-center justify-center text-center"
      persistent
      opacity="0.9"
      style="z-index: 99999;"
    >
      <v-card color="white" elevation="10" min-width="320" max-width="500" class="pa-6 rounded-lg">
        <v-icon color="red-darken-2" size="64" class="mb-4">mdi-cloud-download</v-icon>
        <h2 class="text-h5 mb-2 font-weight-bold text-red-darken-2">New Version Available</h2>
        <p class="mb-6 text-body-1">
          A new version of the application is available. <br>
          Please update to get the latest features and fixes.
        </p>
        
        <div class="d-flex justify-center flex-column gap-2">
           <v-chip color="grey-lighten-2" class="mb-4 align-self-center" variant="flat">
             Version: {{ currentVersion }} → {{ remoteVersion }}
           </v-chip>
           
           <v-btn
            color="red-darken-2"
            size="large"
            block
            elevation="2"
            @click="performUpdate"
            :loading="updating"
          >
            Update Now
          </v-btn>
        </div>
      </v-card>
    </v-overlay>
  </v-app>
</template>

<script lang="ts" setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { vendorStore } from '@/store/vendor';
import axios from 'axios';
import PageLoader from '@/components/PageLoader.vue';

const useVendorStore = vendorStore();
const updateAvailable = ref(false);
const updating = ref(false);
const currentVersion = ref(__APP_VERSION__ || 'Unknown');
const remoteVersion = ref('');
let pollInterval: any = null;

// =========================================================
// ⚙️ SETTING: Poll Interval (in milliseconds)
// Currently: 10 seconds for testing 10000
// Recommended for Production: 60000 (1 minute) or 300000 (5 minutes) or 600000 (10 minutes)
// =========================================================
const POLL_INTERVAL = 60000; 

const checkForUpdate = async () => {
  try {
    // Determine the base URL for the version file
    const versionUrl = `/version.json?t=${Date.now()}`;
    
    const response = await axios.get(versionUrl);
    
    if (response.data && response.data.version) {
      const serverVersion = response.data.version;
      
      // Compare versions
      if (serverVersion !== currentVersion.value) {
        console.log(`🚀 New version detected: ${serverVersion} (Current: ${currentVersion.value})`);
        remoteVersion.value = serverVersion;
        updateAvailable.value = true;
      }
    }
  } catch (error) {
    // Silent fail on network error
  }
};

const performUpdate = () => {
  updating.value = true;
  (useVendorStore as any).clearAndReload();
};

onMounted(() => {
  // Show global loader on initial mount
  useVendorStore.globalLoading = true;
  
  // Initial check
  checkForUpdate();
  
  // Start polling
  pollInterval = setInterval(checkForUpdate, POLL_INTERVAL);
  
  console.log(`📱 App Version Running: ${currentVersion.value}`);
  
  // Set main color CSS variable for global scrollbar
  const mainColor = (useVendorStore.resturant as any)?.main_color || '#d7171c';
  document.documentElement.style.setProperty('--main-color', mainColor);

  // Smart loader: show loader until vendor data is populated
  // Minimum display: 500ms, Maximum wait: 5000ms
  const startTime = Date.now();
  const MIN_LOADER_MS = 500;
  const MAX_LOADER_MS = 5000;
  
  const checkVendorLoaded = () => {
    const elapsed = Date.now() - startTime;
    const vendorData = useVendorStore.resturant as any;
    const isLoaded = vendorData && vendorData.id && vendorData.name;
    
    if (isLoaded && elapsed >= MIN_LOADER_MS) {
      // Data loaded and minimum time passed
      useVendorStore.globalLoading = false;
    } else if (elapsed >= MAX_LOADER_MS) {
      // Timeout - hide loader anyway to prevent infinite loading
      console.log('⏱️ Loader timeout reached, hiding loader');
      useVendorStore.globalLoading = false;
    } else {
      // Check again in 100ms
      setTimeout(checkVendorLoaded, 100);
    }
  };
  
  // Start checking
  setTimeout(checkVendorLoaded, MIN_LOADER_MS);
});

onBeforeUnmount(() => {
  if (pollInterval) clearInterval(pollInterval);
});

// Watch for vendor color changes and update CSS variable
import { watchEffect } from 'vue';
watchEffect(() => {
  const mainColor = (useVendorStore.resturant as any)?.main_color || '#d7171c';
  document.documentElement.style.setProperty('--main-color', mainColor);
});
</script>

<style>
/* Global scrollbar styling - Only html should scroll */
html {
  overflow-y: auto !important;
  overflow-x: hidden !important;
  scrollbar-width: thin;
  scrollbar-color: var(--main-color, #d7171c) #f0f0f0;
}

html::-webkit-scrollbar {
  width: 6px !important;
}

html::-webkit-scrollbar-thumb {
  background: var(--main-color, #d7171c);
  border-radius: 10px;
}

html::-webkit-scrollbar-thumb:hover {
  background: var(--main-color, #d7171c);
  filter: brightness(0.9);
}

html::-webkit-scrollbar-track {
  background: #f0f0f0;
  border-radius: 10px;
}

/* Prevent double scrollbars - body inherits html scroll */
body {
  overflow: visible !important;
  height: auto !important;
  min-height: 100% !important;
}

.v-application {
  overflow: visible !important;
  min-height: 100vh;
}

.v-application__wrap {
  min-height: 100vh !important;
  overflow: visible !important;
}
</style>
